import { Tile } from '@components/shared/tile.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { cn } from '@lib/utils';
import { useGetCurrentUser } from '@queries/users.query';
import { FC, ReactNode, useMemo } from 'react';

const DAYS_THRESHOLD = 5;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

function isNewUser(createdAt: string | undefined): boolean {
  if (!createdAt) return false;

  const createdDate = new Date(createdAt);
  const now = new Date();
  const daysSinceCreation = (now.getTime() - createdDate.getTime()) / MILLISECONDS_PER_DAY;

  return daysSinceCreation < DAYS_THRESHOLD;
}

type AccordionTileProps = {
  collapsible?: boolean;
  content: ReactNode;
  contentClassName?: string;
  defaultOpen?: boolean;
  hideChevron?: boolean;
  tileClassName?: string;
  title: ReactNode;
  triggerClassName?: string;
  value: string;
};

export const AccordionTile: FC<AccordionTileProps> = (props) => {
  const {
    collapsible,
    content,
    contentClassName,
    defaultOpen: defaultOpenProp,
    hideChevron,
    tileClassName,
    title,
    triggerClassName,
    value,
  } = props;

  const { data: user } = useGetCurrentUser();

  const defaultOpen = useMemo(() => {
    if (defaultOpenProp !== undefined) {
      return defaultOpenProp;
    }
    return isNewUser(user?.createdAt);
  }, [defaultOpenProp, user?.createdAt]);

  return (
    <Tile className={tileClassName ?? 'p-0'}>
      <Accordion
        collapsible={collapsible ?? true}
        defaultValue={defaultOpen ? value : undefined}
        type="single"
      >
        <AccordionItem
          className="border-0"
          value={value}
        >
          <AccordionTrigger
            className={cn(
              'p-4 hover:no-underline text-base font-medium text-gray-800',
              hideChevron && '[&>svg]:hidden',
              triggerClassName,
            )}
          >
            {title}
          </AccordionTrigger>
          <AccordionContent className={cn('pt-0 px-4', contentClassName)}>
            {typeof content === 'string' ? <p className="text-sm text-gray-500">{content}</p> : content}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Tile>
  );
};
